<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { RouterLink, RouterView } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useTenantSwitcher } from '@/application/active-tenant'
import { useLogout, useSessionStore } from '@/application/auth'
import { hasRoleAtLeast, type Role } from '@/domain/auth'
import { PSelect } from '@/design-system'
import LocaleSwitcher from '@/presentation/components/LocaleSwitcher.vue'

/**
 * PAppShell — layout d'app pour les pages authentifiées (dashboard +
 * admin CRUD + me + audit log).
 *
 * Pattern Revelio (cf. REVELIO_DESIGN_SYSTEM §5.8) :
 *   - Sidebar fixe gauche w-56 avec sections groupées :
 *       OBSERVABILITÉ · ROUTING · ACCÈS · SYSTÈME · COMPTE
 *     Chaque section a un eyebrow font-mono uppercase tracking-wider.
 *   - Logo « PreLLM » Changa One italic en haut de la sidebar.
 *   - Footer sidebar : avatar initiales (cercle ink) + email + rôle +
 *     bouton ghost "Sign out".
 *   - Topbar slim (h-12) AU-DESSUS du main, UNIQUEMENT pour super_admin :
 *     contient le sélecteur de tenant actif (filtre cross-tenant côté UI).
 *
 * RBAC visuelle : les items sont filtrés par rôle via `hasRoleAtLeast`.
 * La sécurité réelle reste backend ; on cache simplement pour ne pas
 * leak la possibilité d'accès.
 */

interface NavItem {
    labelKey: string
    to: string
    minRole: Role
}

interface NavSection {
    eyebrowKey: string
    items: NavItem[]
}

/**
 * Navigation organisée par domaine fonctionnel plutôt qu'à plat.
 * Si tous les items d'une section sont cachés par RBAC, la section
 * elle-même est cachée (cf. visibleSections).
 */
const navSections: NavSection[] = [
    {
        eyebrowKey: 'nav.section.observability',
        items: [
            { labelKey: 'nav.dashboard', to: '/', minRole: 'tenant_user' },
            { labelKey: 'nav.auditLog', to: '/admin/audit-log', minRole: 'tenant_master' },
        ],
    },
    {
        eyebrowKey: 'nav.section.routing',
        items: [
            { labelKey: 'nav.providerKeys', to: '/admin/provider-keys', minRole: 'tenant_master' },
            { labelKey: 'nav.apiKeys',      to: '/admin/api-keys',      minRole: 'tenant_master' },
            { labelKey: 'nav.routes',       to: '/admin/routes',        minRole: 'tenant_master' },
        ],
    },
    {
        eyebrowKey: 'nav.section.access',
        items: [
            { labelKey: 'nav.users',   to: '/admin/users',   minRole: 'tenant_master' },
            { labelKey: 'nav.teams',   to: '/admin/teams',   minRole: 'tenant_master' },
            { labelKey: 'nav.tenants', to: '/admin/tenants', minRole: 'super_admin' },
        ],
    },
    {
        eyebrowKey: 'nav.section.reference',
        items: [
            { labelKey: 'nav.documentation', to: '/docs', minRole: 'tenant_user' },
        ],
    },
    {
        eyebrowKey: 'nav.section.account',
        items: [
            { labelKey: 'nav.myAccount', to: '/me', minRole: 'tenant_user' },
        ],
    },
]

const { t } = useI18n()
const sessionStore = useSessionStore()
const { user } = storeToRefs(sessionStore)
const { logout, isLoading: logoutLoading } = useLogout()

const {
    canSwitch: canSwitchTenant,
    tenants: tenantList,
    activeTenantId,
    isOverridden: isTenantOverridden,
    isLoading: tenantsLoading,
    select: selectTenant,
} = useTenantSwitcher()

const visibleSections = computed<NavSection[]>(() => {
    const role = user.value?.role
    if (!role) return []
    return navSections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) => hasRoleAtLeast(role, item.minRole)),
        }))
        .filter((section) => section.items.length > 0)
})

const roleLabel = computed(() => {
    const role = user.value?.role
    return role ? t(`roles.${role}`) : '—'
})

const userInitials = computed(() => {
    const email = user.value?.email ?? ''
    if (!email) return '·'
    const local = email.split('@')[0] ?? ''
    const parts = local.split(/[._-]+/).filter(Boolean)
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return local.slice(0, 2).toUpperCase()
})

async function onLogout(): Promise<void> {
    await logout()
}

function onSelectTenant(value: string): void {
    selectTenant(value === '__all__' ? null : value)
}
</script>

<template>
    <div class="p-app-shell">
        <!-- ═══════════════════════════════════════════════════════
             SIDEBAR
             ═══════════════════════════════════════════════════════ -->
        <aside class="p-app-shell__sidebar" aria-label="Main navigation">
            <div class="p-app-shell__brand">
                <span class="p-app-shell__brand-mark">PreLLM</span>
                <span class="p-app-shell__brand-tag">{{ $t('common.controlPlane') }}</span>
            </div>

            <nav class="p-app-shell__nav">
                <div
                    v-for="section in visibleSections"
                    :key="section.eyebrowKey"
                    class="p-app-shell__section"
                >
                    <p class="p-app-shell__section-eyebrow">{{ $t(section.eyebrowKey) }}</p>
                    <RouterLink
                        v-for="item in section.items"
                        :key="item.to"
                        :to="item.to"
                        class="p-app-shell__link"
                        active-class="p-app-shell__link--active"
                    >
                        {{ $t(item.labelKey) }}
                    </RouterLink>
                </div>
            </nav>

            <div v-if="user" class="p-app-shell__identity">
                <div class="p-app-shell__identity-row">
                    <div class="p-app-shell__avatar" aria-hidden="true">
                        {{ userInitials }}
                    </div>
                    <div class="p-app-shell__identity-info">
                        <p class="p-app-shell__identity-email">{{ user.email }}</p>
                        <p class="p-app-shell__identity-meta">
                            {{ roleLabel }} · {{ user.tenantId }}
                        </p>
                    </div>
                </div>
                <div class="p-app-shell__identity-actions">
                    <button
                        type="button"
                        class="p-app-shell__signout"
                        :disabled="logoutLoading"
                        data-test="logout"
                        @click="onLogout"
                    >
                        {{ logoutLoading ? $t('nav.signingOut') : $t('nav.signOut') }}
                    </button>
                    <LocaleSwitcher />
                </div>
            </div>
        </aside>

        <!-- ═══════════════════════════════════════════════════════
             MAIN + TOPBAR optionnelle (super_admin uniquement)
             ═══════════════════════════════════════════════════════ -->
        <div class="p-app-shell__main-wrap">
            <header
                v-if="canSwitchTenant"
                class="p-app-shell__topbar"
                aria-label="Super admin context bar"
            >
                <div class="p-app-shell__topbar-context">
                    <span class="p-app-shell__topbar-eyebrow">{{ $t('topbar.tenantView') }}</span>
                    <PSelect
                        class="p-app-shell__tenant-select"
                        size="sm"
                        :model-value="activeTenantId ?? '__all__'"
                        :disabled="tenantsLoading"
                        :aria-label="$t('topbar.selectActiveTenant')"
                        @change="onSelectTenant"
                    >
                        <option value="__all__">{{ $t('topbar.allTenants') }}</option>
                        <option v-for="tenant in tenantList" :key="tenant.id" :value="tenant.id">
                            {{ tenant.name }} · {{ tenant.id }}
                        </option>
                    </PSelect>
                    <span
                        v-if="isTenantOverridden"
                        class="p-app-shell__topbar-flag"
                    >
                        <span class="p-app-shell__topbar-dot"></span>
                        {{ $t('topbar.filteredContext') }}
                    </span>
                </div>
                <div class="p-app-shell__topbar-spacer"></div>
            </header>

            <main class="p-app-shell__main">
                <RouterView v-slot="{ Component }">
                    <Transition name="fade" mode="out-in">
                        <component :is="Component" />
                    </Transition>
                </RouterView>
            </main>
        </div>
    </div>
</template>

<style scoped>
.p-app-shell {
    display: flex;
    min-height: 100vh;
    background: var(--p-color-bg);
}

/* ═══════════════ SIDEBAR ═══════════════ */
.p-app-shell__sidebar {
    width: 224px;
    flex: 0 0 auto;
    background: var(--p-color-surface);
    border-right: 1px solid var(--p-color-line);
    display: flex;
    flex-direction: column;
    height: 100vh;
    position: sticky;
    top: 0;
}

.p-app-shell__brand {
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--p-color-line);
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.p-app-shell__brand-mark {
    font-family: var(--p-font-logo);
    font-style: italic;
    font-size: 22px;
    color: var(--p-color-ink);
    letter-spacing: -0.5px;
    line-height: 1;
}
.p-app-shell__brand-tag {
    font-family: var(--p-font-mono);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-widest);
    color: var(--p-color-ink-5);
}

.p-app-shell__nav {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.p-app-shell__section {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.p-app-shell__section-eyebrow {
    margin: 0 0 6px 12px;
    font-family: var(--p-font-mono);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-widest);
    color: var(--p-color-ink-5);
}

.p-app-shell__link {
    display: block;
    padding: 7px 12px;
    border-radius: var(--p-radius-sm);
    color: var(--p-color-ink-4);
    text-decoration: none;
    font-size: var(--p-text-sm);
    font-weight: var(--p-font-weight-normal);
    transition:
        background var(--p-motion-fast) var(--p-ease-out),
        color var(--p-motion-fast) var(--p-ease-out);
}
.p-app-shell__link:hover {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
}
.p-app-shell__link--active {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink);
    font-weight: var(--p-font-weight-medium);
}

.p-app-shell__identity {
    border-top: 1px solid var(--p-color-line);
    padding: 14px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.p-app-shell__identity-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.p-app-shell__avatar {
    width: 30px;
    height: 30px;
    border-radius: var(--p-radius-full);
    background: var(--p-color-ink);
    color: var(--p-color-surface);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--p-font-mono);
    font-size: 10px;
    font-weight: var(--p-font-weight-medium);
    flex-shrink: 0;
    letter-spacing: 0;
}
.p-app-shell__identity-info {
    min-width: 0;
    flex: 1;
}
.p-app-shell__identity-email {
    margin: 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink);
    font-weight: var(--p-font-weight-medium);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.p-app-shell__identity-meta {
    margin: 1px 0 0;
    font-family: var(--p-font-mono);
    font-size: 9px;
    color: var(--p-color-ink-5);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.p-app-shell__identity-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--p-space-2);
    margin-top: var(--p-space-2);
}
.p-app-shell__signout {
    background: transparent;
    border: 1px solid var(--p-color-line);
    color: var(--p-color-ink-4);
    cursor: pointer;
    padding: 6px 10px;
    border-radius: var(--p-radius-sm);
    font-family: var(--p-font-sans);
    font-size: var(--p-text-xs);
    transition:
        color var(--p-motion-fast) var(--p-ease-out),
        border-color var(--p-motion-fast) var(--p-ease-out);
}
.p-app-shell__signout:hover:not(:disabled) {
    color: var(--p-color-ink);
    border-color: var(--p-color-ink-6);
}
.p-app-shell__signout:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ═══════════════ MAIN + TOPBAR ═══════════════ */
.p-app-shell__main-wrap {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.p-app-shell__topbar {
    height: 44px;
    background: var(--p-color-surface);
    border-bottom: 1px solid var(--p-color-line);
    display: flex;
    align-items: center;
    padding: 0 24px;
    gap: 16px;
    flex-shrink: 0;
}
.p-app-shell__topbar-context {
    display: flex;
    align-items: center;
    gap: 12px;
}
.p-app-shell__topbar-eyebrow {
    font-family: var(--p-font-mono);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-widest);
    color: var(--p-color-ink-5);
    margin: 0;
}

.p-app-shell__tenant-select {
    min-width: 220px;
}

.p-app-shell__topbar-flag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--p-font-mono);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-olive);
}
.p-app-shell__topbar-dot {
    width: 6px;
    height: 6px;
    border-radius: var(--p-radius-full);
    background: var(--p-color-olive);
}

.p-app-shell__topbar-spacer { flex: 1; }

.p-app-shell__main {
    flex: 1 1 auto;
    overflow-x: auto;
    overflow-y: auto;
}
</style>
